import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixSocialGroupsV2GroupRequest from './social-groups-v2-group-request-group-requests.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/**
 * A group request is a site member's request for a site admin to create a group.
 * You can approve, reject, or view group requests.
 * Learn more about [Wix Groups](https://support.wix.com/en/article/wix-groups-about-groups).
 */
export interface GroupRequest {
  /**
   * Group request ID.
   * @readonly
   * @format GUID
   */
  _id?: string | null;
  /** Group requested for creation. */
  group?: Group;
  /** Status of group request. */
  status?: RequestStatusWithLiterals;
  /** Group request details. */
  requestDetails?: RequestDetails;
}

/**
 * A group object represents a community space where site members can connect and share content.
 * You can create groups and manage their visibility, settings, and metadata.
 * Learn more about [groups](https://support.wix.com/en/article/wix-groups-about-groups).
 */
export interface Group {
  /**
   * Group ID.
   * @readonly
   * @format GUID
   */
  _id?: string | null;
  /**
   * A unique part of a group's URL, for example `https:/example.com/groups/slug`.
   * @minLength 1
   * @maxLength 100
   */
  slug?: string | null;
  /** Group privacy status. */
  privacyStatus?: PrivacyStatusWithLiterals;
  /**
   * Group name.
   * @maxLength 100
   */
  name?: string | null;
  /**
   * Group description in [DraftJS](https://draftjs.org) format.
   * @maxLength 20480
   */
  description?: string | null;
  /**
   * Group teaser.
   * @maxLength 1000
   */
  teaser?: string | null;
  /**
   * What group members are called, for example `Coworkers`, `Friends`, or `Students`.
   * @minLength 1
   * @maxLength 1000
   */
  memberTitle?: string | null;
  /** Cover image. You cannot upload your own cover image. */
  coverImage?: CoverImage;
  /**
   * Group specific settings.
   *
   * These settings can also be found in [a site's Dashboard](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Fwix-groups/settings?).
   */
  settings?: GroupSettings;
  /**
   * Total count of current group members.
   * @readonly
   */
  membersCount?: number | null;
  /**
   * Group owner.
   * @readonly
   * @format GUID
   */
  ownerId?: string | null;
  /**
   * Group creation date and time.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time of the latest group update.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Date and time of the most recent group activity, for example a post or comment.
   * @readonly
   */
  lastActivityDate?: Date | null;
}

export enum Type {
  UNKNOWN = 'UNKNOWN',
  ADMIN_APPROVAL = 'ADMIN_APPROVAL',
  PAID_PLANS = 'PAID_PLANS',
  EVENTS = 'EVENTS',
}

/** @enumType */
export type TypeWithLiterals =
  | Type
  | 'UNKNOWN'
  | 'ADMIN_APPROVAL'
  | 'PAID_PLANS'
  | 'EVENTS';

export interface Events {
  /** @format GUID */
  eventIds?: string[];
}

export interface Logo {
  /**
   * Logo image ID (for internal use).
   * @maxLength 1000
   */
  mediaId?: string | null;
  /** Logo image width. */
  width?: number | null;
  /** Logo image height. */
  height?: number | null;
  /**
   * Alternative text.
   * @maxLength 200
   */
  altText?: string | null;
}

export interface GroupDetailsPosition {
  /** horizontal coordinate */
  x?: number;
  /** vertical coordinate */
  y?: number;
}

export interface Image {
  /**
   * Image ID (for internal use).
   * @maxLength 1000
   */
  mediaId?: string | null;
  /** Image width. */
  width?: number | null;
  /** Image height. */
  height?: number | null;
  /** Indicates pre-configured/auto-generated images (from templates, client generated). */
  preset?: boolean | null;
}

export interface Position {
  /** horizontal coordinate */
  x?: number;
  /** vertical coordinate */
  y?: number;
}

export enum AllowPolicy {
  UNKNOWN = 'UNKNOWN',
  OWNER_AND_ADMINS = 'OWNER_AND_ADMINS',
  OWNER = 'OWNER',
  ALL_MEMBERS = 'ALL_MEMBERS',
}

/** @enumType */
export type AllowPolicyWithLiterals =
  | AllowPolicy
  | 'UNKNOWN'
  | 'OWNER_AND_ADMINS'
  | 'OWNER'
  | 'ALL_MEMBERS';

export interface OnboardingStepSettings {
  stepKey?: StepKeyWithLiterals;
  visible?: boolean;
}

export enum StepKey {
  UNKNOWN = 'UNKNOWN',
  CREATE_POST = 'CREATE_POST',
  REACT_TO_POST = 'REACT_TO_POST',
  INVITE_MEMBERS = 'INVITE_MEMBERS',
}

/** @enumType */
export type StepKeyWithLiterals =
  | StepKey
  | 'UNKNOWN'
  | 'CREATE_POST'
  | 'REACT_TO_POST'
  | 'INVITE_MEMBERS';

export enum PrivacyStatus {
  /** Undefined group privacy status. */
  UNKNOWN = 'UNKNOWN',
  /** Anyone can see the group and its content. Anyone can join the group. */
  PUBLIC = 'PUBLIC',
  /** Anyone can see the group, but only members can see its content. New members must submit a `Join Group Request`. */
  PRIVATE = 'PRIVATE',
  /** Only admins and members can see the group. New members can only be added by other members. */
  SECRET = 'SECRET',
}

/** @enumType */
export type PrivacyStatusWithLiterals =
  | PrivacyStatus
  | 'UNKNOWN'
  | 'PUBLIC'
  | 'PRIVATE'
  | 'SECRET';

export interface AccessRestriction extends AccessRestrictionDataOneOf {
  events?: Events;
  type?: TypeWithLiterals;
}

/** @oneof */
export interface AccessRestrictionDataOneOf {
  events?: Events;
}

export interface GroupDetails {
  /** Group logo. You cannot upload your own logo. */
  logo?: Logo;
  /**
   * What group members are called, for example `Coworkers`, `Friends`, or `Students`.
   * @minLength 1
   * @maxLength 1000
   */
  membersTitle?: string | null;
}

/** Cover image. You cannot upload your own cover image. */
export interface CoverImage {
  /** Cover image. */
  image?: Image;
  /** Position of the corner of the cover image (or logo). */
  position?: Position;
  /** Position of the corner of the cover image (or logo) for mobile browser. */
  mobilePosition?: Position;
  /**
   * Alternative text is typically a relatively short phrase that describes what the image depicts.
   *
   * The alternative text is used:
   * + If the browser cannot display the image.
   * + If the user is utilizing a screen reader.
   * + By search engines to understand what images are on your site.
   * @maxLength 200
   */
  altText?: string | null;
}

export interface GroupSettings {
  /**
   * __Deprecated.__ Use `allowedToInviteMembers` instead.
   * Whether regular members are permitted to invite new members.
   * If `false`, only admins can invite members. Defaults to `true`.
   * @deprecated
   */
  membersCanInvite?: boolean | null;
  /**
   * __Deprecated.__ Use `allowedToApproveJoinRequests` instead.
   * Whether all group members are permitted to approve join group requests.
   * If `false`, member approval is limited to the admins.
   * @deprecated
   */
  membersCanApprove?: boolean | null;
  /** Whether a daily post about new members is enabled. */
  welcomeMemberPostEnabled?: boolean | null;
  /** Whether an automatic post about changing the group details is enabled. */
  groupDetailsChangedPostEnabled?: boolean | null;
  /** Whether all members can see the full member list. */
  showMemberList?: boolean | null;
  /** Determines who can invite members to the group */
  allowedToInviteMembers?: AllowPolicyWithLiterals;
  /** Determines who can approve member join requests to the group */
  allowedToApproveJoinRequests?: AllowPolicyWithLiterals;
  /**
   * Whether AI spam protection is enabled for post creation in the group.
   * If not explicitly set, the default behavior is 'true' (enabled).
   */
  aiSpamProtectionEnabled?: boolean | null;
}

export interface Identity {
  /**
   * Member ID of the group creator.  See the Members API for more details.
   * @format GUID
   */
  _id?: string | null;
  identityType?: IdentityTypeWithLiterals;
}

export enum IdentityType {
  /** Wix user. */
  USER = 'USER',
  /** Wix member. */
  MEMBER = 'MEMBER',
}

/** @enumType */
export type IdentityTypeWithLiterals = IdentityType | 'USER' | 'MEMBER';

export enum RequestStatus {
  /** Pending group request. */
  PENDING = 'PENDING',
  /** Approved group request. */
  APPROVED = 'APPROVED',
  /** Rejected group request. */
  REJECTED = 'REJECTED',
  /** Canceled group request. */
  CANCELED = 'CANCELED',
}

/** @enumType */
export type RequestStatusWithLiterals =
  | RequestStatus
  | 'PENDING'
  | 'APPROVED'
  | 'REJECTED'
  | 'CANCELED';

export interface RequestDetails {
  /**
   * Reason the request has been rejected.
   * @maxLength 1000
   */
  rejectionReason?: string | null;
}

export interface SubmitGroupRequestRequest {
  /** Group submitted for creation. */
  group?: Group;
  /** Content type of group description. */
  contentType?: ContentTypeWithLiterals;
}

export enum ContentType {
  PLAIN_TEXT = 'PLAIN_TEXT',
  DRAFTJS = 'DRAFTJS',
  RICH_CONTENT = 'RICH_CONTENT',
}

/** @enumType */
export type ContentTypeWithLiterals =
  | ContentType
  | 'PLAIN_TEXT'
  | 'DRAFTJS'
  | 'RICH_CONTENT';

export interface SubmitGroupRequestResponse {
  /** Submitted Group Request. */
  groupRequest?: GroupRequest;
}

export interface ApproveGroupRequestsRequest {
  /**
   * Group request IDs to approve. Limited to `100`.
   * @maxSize 100
   */
  groupRequestIds?: string[];
}

export enum ItemsToUpdate {
  /** Take into account only items which are listed in the request. */
  BY_ID = 'BY_ID',
  /** Update all items. */
  ALL_ITEMS = 'ALL_ITEMS',
}

/** @enumType */
export type ItemsToUpdateWithLiterals = ItemsToUpdate | 'BY_ID' | 'ALL_ITEMS';

export interface ApproveGroupRequestsResponse {
  /** Approved group requests. */
  groupRequest?: GroupRequest[];
}

export interface GroupRequestApproved {
  /** Approved group request. */
  groupRequest?: GroupRequest;
}

export interface RejectGroupRequestsRequest {
  /**
   * Rejection data.
   * @maxSize 100
   */
  rejections?: Rejection[];
}

export interface Rejection {
  /**
   * ID of the group request to reject.
   * @format GUID
   */
  groupRequestId?: string;
  /**
   * Rejection reason. Free text displayed to the creator of the rejected group request. Limited to 1,000 characters.
   * @maxLength 1000
   */
  reason?: string | null;
}

export interface RejectGroupRequestsResponse {
  /** Rejected group requests. */
  groupRequest?: GroupRequest[];
}

export interface GroupRequestRejected {
  /** Rejected group request. */
  groupRequest?: GroupRequest;
}

export interface CancelGroupRequestRequest {
  /**
   * ID of the group request to cancel.
   * @format GUID
   */
  groupRequestId?: string;
}

export interface CancelGroupRequestResponse {
  /** Canceled group request. */
  groupRequest?: GroupRequest;
}

export interface ListGroupRequestsRequest {
  /**
   * Number of items to load. Maximum `100`.
   * @max 100
   */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export enum OwnershipFilter {
  /** All items. */
  ALL = 'ALL',
  /** Items for the current site member. */
  CURRENT_MEMBER = 'CURRENT_MEMBER',
}

/** @enumType */
export type OwnershipFilterWithLiterals =
  | OwnershipFilter
  | 'ALL'
  | 'CURRENT_MEMBER';

export interface ListGroupRequestsResponse {
  /** Group Requests. */
  groupRequests?: GroupRequest[];
  /** Paging information. */
  metadata?: PagingMetadata;
}

export interface PagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
}

export interface QueryGroupRequestsRequest {
  query?: Query;
}

export interface Query {
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: any;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   */
  sort?: Sorting[];
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /** Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned. */
  fields?: string[];
  /** Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned. */
  fieldsets?: string[];
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface Paging {
  /**
   * Number of items to load.
   *
   * Max: `100`
   */
  limit?: number | null;
  /**
   * Number of items to skip in the current sort order.
   *
   */
  offset?: number | null;
}

export interface QueryGroupRequestsResponse {
  /** Retrieved group requests. */
  groupRequests?: GroupRequest[];
  /** Paging information. */
  metadata?: PagingMetadata;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface GroupRequestApprovedEnvelope {
  data: GroupRequestApproved;
  metadata: EventMetadata;
}

/**
 * Triggered when a group request is approved.
 * @permissionScope Manage Social Groups
 * @permissionScopeId SCOPE.DC-MANAGE.SOCIAL-GROUPS
 * @permissionId SOCIAL-GROUPS.MANAGE
 * @webhook
 * @eventType wix.social_groups.v2.group_request_approved
 * @serviceIdentifier wix.social.groups.api.v2.GroupRequestsService
 * @slug approved
 */
export declare function onGroupRequestApproved(
  handler: (event: GroupRequestApprovedEnvelope) => void | Promise<void>
): void;

export interface GroupRequestCreatedEnvelope {
  entity: GroupRequest;
  metadata: EventMetadata;
}

/** @permissionScope Manage Social Groups
 * @permissionScopeId SCOPE.DC-MANAGE.SOCIAL-GROUPS
 * @permissionId SOCIAL-GROUPS.MANAGE
 * @webhook
 * @eventType wix.social_groups.v2.group_request_created
 * @serviceIdentifier wix.social.groups.api.v2.GroupRequestsService
 * @slug created
 * @documentationMaturity preview
 */
export declare function onGroupRequestCreated(
  handler: (event: GroupRequestCreatedEnvelope) => void | Promise<void>
): void;

export interface GroupRequestRejectedEnvelope {
  data: GroupRequestRejected;
  metadata: EventMetadata;
}

/**
 * Triggered when a group request is rejected.
 * @permissionScope Manage Social Groups
 * @permissionScopeId SCOPE.DC-MANAGE.SOCIAL-GROUPS
 * @permissionId SOCIAL-GROUPS.MANAGE
 * @webhook
 * @eventType wix.social_groups.v2.group_request_rejected
 * @serviceIdentifier wix.social.groups.api.v2.GroupRequestsService
 * @slug rejected
 */
export declare function onGroupRequestRejected(
  handler: (event: GroupRequestRejectedEnvelope) => void | Promise<void>
): void;

/**
 * Approves group requests.
 *
 * Only Wix users can approve group requests.
 * @param groupRequestIds - Group request IDs to approve. Limited to `100`.
 * @public
 * @requiredField groupRequestIds
 * @fqn wix.social.groups.api.v2.GroupRequestsService.ApproveGroupRequests
 */
export async function approveGroupRequests(
  groupRequestIds: string[]
): Promise<
  NonNullablePaths<
    ApproveGroupRequestsResponse,
    | `groupRequest`
    | `groupRequest.${number}.group.privacyStatus`
    | `groupRequest.${number}.group.settings.allowedToInviteMembers`
    | `groupRequest.${number}.group.settings.allowedToApproveJoinRequests`
    | `groupRequest.${number}.status`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    groupRequestIds: groupRequestIds,
  });

  const reqOpts =
    ambassadorWixSocialGroupsV2GroupRequest.approveGroupRequests(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { groupRequestIds: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['groupRequestIds']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Rejects group requests.
 *
 * Only Wix users can reject group requests.
 * @param rejections - Rejection data.
 * @public
 * @requiredField rejections
 * @fqn wix.social.groups.api.v2.GroupRequestsService.RejectGroupRequests
 */
export async function rejectGroupRequests(
  rejections: Rejection[]
): Promise<
  NonNullablePaths<
    RejectGroupRequestsResponse,
    | `groupRequest`
    | `groupRequest.${number}.group.privacyStatus`
    | `groupRequest.${number}.group.settings.allowedToInviteMembers`
    | `groupRequest.${number}.group.settings.allowedToApproveJoinRequests`
    | `groupRequest.${number}.status`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    rejections: rejections,
  });

  const reqOpts =
    ambassadorWixSocialGroupsV2GroupRequest.rejectGroupRequests(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { rejections: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['rejections']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Lists requests to create a group.
 *
 * The `listGroupRequests()` function returns a Promise that resolves to a list of up to 1,000 create group requests on your site.
 *
 * Using the options parameter, you can filter your list of posts, set the amount of posts to be returned, and sort your list in a specified order.
 *
 * By default, the list is sorted by `_createdDate` in descending order. Only admins can see create group requests. Members can access their own create group requests in their site.
 *
 * >**Note:** This function is only relevant if admin approval is required for creating a group.
 *
 * @public
 * @param options - Paging options.
 * @fqn wix.social.groups.api.v2.GroupRequestsService.ListGroupRequests
 */
export async function listGroupRequests(
  options?: ListGroupRequestsOptions
): Promise<
  NonNullablePaths<
    ListGroupRequestsResponse,
    | `groupRequests`
    | `groupRequests.${number}.group.privacyStatus`
    | `groupRequests.${number}.group.settings.allowedToInviteMembers`
    | `groupRequests.${number}.group.settings.allowedToApproveJoinRequests`
    | `groupRequests.${number}.status`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    limit: options?.limit,
    offset: options?.offset,
  });

  const reqOpts =
    ambassadorWixSocialGroupsV2GroupRequest.listGroupRequests(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          limit: '$[0].limit',
          offset: '$[0].offset',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListGroupRequestsOptions {
  /**
   * Number of items to load.
   *
   * Max: `100`
   *
   * @max 100
   */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

/**
 * Creates a query to retrieve a list of create requests.
 *
 * The `queryGroupRequests()` function builds a query to retrieve a list of all requests to create a group, and returns a `GroupRequestsQueryBuilder` object.
 *
 * The returned object contains the query definition which is typically used to run the query using the [`find()`](/group-requests-query-builder/find) function. You can refine the query by chaining `GroupRequestQueryBuilder` functions onto the query. `GroupRequestQueryBuilder` functions enable you to sort, filter, and control the results that `queryGroupRequests()` returns.
 *
 * `queryGroupRequests()` runs with these `GroupRequestsQueryBuilder` defaults, which you can override:
 * - [`limit(1000)`](/group-requests-query-builder/limit)
 *
 * The following `GroupRequestsQueryBuilder` functions are supported for `queryGroupRequests()`. For a full description of the `GroupRequests` object, see the object returned for the [`items`](/group-requests-query-result/items) property in `GroupRequestsQueryResult`.
 * @public
 * @fqn wix.social.groups.api.v2.GroupRequestsService.QueryGroupRequests
 */
export function queryGroupRequests(
  options?: QueryGroupRequestsOptions
): GroupRequestsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    GroupRequest,
    'OFFSET',
    QueryGroupRequestsRequest,
    QueryGroupRequestsResponse
  >({
    func: async (payload: QueryGroupRequestsRequest) => {
      const reqOpts =
        ambassadorWixSocialGroupsV2GroupRequest.queryGroupRequests({
          ...payload,
          ...(options ?? {}),
        });

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryGroupRequestsRequest['query']) => {
      const args = [query, options] as [
        QueryGroupRequestsRequest['query'],
        QueryGroupRequestsOptions
      ];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryGroupRequestsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.groupRequests,
        pagingMetadata: transformedData?.metadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'OFFSET',
    transformationPaths: {},
  });
}

export interface QueryGroupRequestsOptions {}

interface QueryOffsetResult {
  currentPage: number | undefined;
  totalPages: number | undefined;
  totalCount: number | undefined;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface GroupRequestsQueryResult extends QueryOffsetResult {
  items: GroupRequest[];
  query: GroupRequestsQueryBuilder;
  next: () => Promise<GroupRequestsQueryResult>;
  prev: () => Promise<GroupRequestsQueryResult>;
}

export interface GroupRequestsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (propertyName: 'status', value: any) => GroupRequestsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (propertyName: 'status', value: any) => GroupRequestsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasSome: (propertyName: 'status', value: any[]) => GroupRequestsQueryBuilder;
  in: (propertyName: 'status', value: any) => GroupRequestsQueryBuilder;
  exists: (propertyName: 'status', value: boolean) => GroupRequestsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => GroupRequestsQueryBuilder;
  /** @param skip - Number of items to skip in the query results before returning the results. */
  skip: (skip: number) => GroupRequestsQueryBuilder;
  find: () => Promise<GroupRequestsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.social.groups.api.v2.GroupRequestsService.QueryGroupRequests
 * @requiredField query
 */
export async function typedQueryGroupRequests(
  query: GroupRequestQuery,
  options?: QueryGroupRequestsOptions
): Promise<
  NonNullablePaths<
    QueryGroupRequestsResponse,
    | `groupRequests`
    | `groupRequests.${number}.group.privacyStatus`
    | `groupRequests.${number}.group.settings.allowedToInviteMembers`
    | `groupRequests.${number}.group.settings.allowedToApproveJoinRequests`
    | `groupRequests.${number}.status`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    query: query,
    ...options,
  });

  const reqOpts =
    ambassadorWixSocialGroupsV2GroupRequest.queryGroupRequests(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GroupRequestQuerySpec extends QuerySpec {
  paging: 'offset';
  wql: [];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  GroupRequest,
  GroupRequestQuerySpec
>;
export type GroupRequestQuery = {
  /** 
  Filter object in the following format:
  `"filter" : {
  "fieldName1": "value1",
  "fieldName2":{"$operator":"value2"}
  }`
  Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`  
  */
  filter?: CommonQueryWithEntityContext['filter'];
  /** 
  Sort object in the following format:
  `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`  
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
  /** 
  Paging options to limit and skip the number of items.  
  */
  paging?: {
    /** 
  Number of items to load. 

  Max: `100`  
  */
    limit?: NonNullable<CommonQueryWithEntityContext['paging']>['limit'] | null;
    /** 
  Number of items to skip in the current sort order.  
  */
    offset?:
      | NonNullable<CommonQueryWithEntityContext['paging']>['offset']
      | null;
  };
};

export const utils = {
  ...createQueryUtils<GroupRequest, GroupRequestQuerySpec, GroupRequestQuery>(),
};
